
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useLocation } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Artwork {
  id: number;
  title: string;
  artist: string;
  technique: string;
  category: string;
  size: string;
  price: string;
  year: string;
  materials: string;
  description: string;
  image_url?: string;
  images?: string[];
  created_at: string;
}

interface CheckoutProps {
  cartItems?: number[];
  onClose?: () => void;
}

export default function CheckoutPage({ cartItems: propCartItems = [], onClose }: CheckoutProps) {
  const location = useLocation();
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Sepet verilerini location.state, localStorage veya props'tan al
  const [cartItems, setCartItems] = useState<number[]>(() => {
    // Önce location.state'den kontrol et
    const stateCartItems = location.state?.cartItems;
    if (stateCartItems && Array.isArray(stateCartItems) && stateCartItems.length > 0) {
      return stateCartItems;
    }
    
    // Sonra localStorage'dan kontrol et
    try {
      const savedCartItems = localStorage.getItem('cartItems');
      if (savedCartItems) {
        const parsedItems = JSON.parse(savedCartItems);
        if (Array.isArray(parsedItems) && parsedItems.length > 0) {
          return parsedItems;
        }
      }
    } catch (error) {
      console.error('Error parsing cart items from localStorage:', error);
    }
    
    // Son olarak props'tan al
    return propCartItems;
  });

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    paymentMethod: 'bank-transfer',
    notes: ''
  });

  // Mock data as fallback
  const mockArtworks = [
    {
      id: 1,
      title: "Gündoğumu Yansımaları",
      artist: "Mehmet Aksoy",
      technique: "Yağlı Boya",
      category: "oil",
      size: "70x50 cm",
      price: "8500",
      year: "2023",
      materials: "Tuval üzerine yağlı boya",
      description: "Bu eser, doğanın en büyüleyici anlarından biri olan gündoğumunu yansıtır.",
      image_url: "https://public.readdy.ai/ai/img_res/749692079314eebeda52282f6a72e896.jpg",
      created_at: "2024-01-01"
    },
    {
      id: 2,
      title: "Sakin Orman",
      artist: "Ayşe Demir",
      technique: "Sulu Boya",
      category: "watercolor",
      size: "60x40 cm",
      price: "4200",
      year: "2024",
      materials: "Sulu boya kağıdı üzerine sulu boya",
      description: "Ormanın derinliklerinde yakalanan bu sessizlik, sulu boyanın şeffaflığı ile mükemmel bir uyum içinde sunulmuştur.",
      image_url: "https://readdy.ai/api/search-image?query=Peaceful%20forest%20landscape%20watercolor%20painting%2C%20transparent%20watercolor%20technique%20showing%20tall%20trees%20and%20dappled%20sunlight%2C%20soft%20green%20and%20blue%20watercolor%20washes%2C%20delicate%20watercolor%20paper%20texture%2C%20serene%20woodland%20scene%20with%20flowing%20watercolor%20effects&width=400&height=500&seq=checkout2&orientation=portrait",
      created_at: "2024-01-02"
    },
    {
      id: 3,
      title: "Şehir Işıkları",
      artist: "Can Yılmaz",
      technique: "Yağlı Boya",
      category: "oil",
      size: "80x60 cm",
      price: "12000",
      year: "2023",
      materials: "Tuval üzerine yağlı boya",
      description: "Modern şehir yaşamının dinamizmini yansıtan bu eser, gece ışıklarının büyüsünü tuval üzerine taşır.",
      image_url: "https://readdy.ai/api/search-image?query=Urban%20city%20lights%20oil%20painting%20at%20night%2C%20vibrant%20city%20illumination%20with%20colorful%20neon%20reflections%2C%20contemporary%20oil%20painting%20technique%20with%20bold%20brushstrokes%2C%20dynamic%20urban%20landscape%20with%20glowing%20street%20lights%20and%20buildings%2C%20modern%20cityscape%20artwork&width=400&height=500&seq=checkout3&orientation=portrait",
      created_at: "2024-01-03"
    }
  ];

  useEffect(() => {
    fetchArtworks();
    
    // URL state'den sepet verilerini kontrol et
    const stateCartItems = location.state?.cartItems;
    if (stateCartItems && Array.isArray(stateCartItems) && stateCartItems.length > 0) {
      setCartItems(stateCartItems);
      // localStorage'a da kaydet
      localStorage.setItem('cartItems', JSON.stringify(stateCartItems));
    } else {
      // localStorage'dan kontrol et
      try {
        const savedCartItems = localStorage.getItem('cartItems');
        if (savedCartItems) {
          const parsedItems = JSON.parse(savedCartItems);
          if (Array.isArray(parsedItems) && parsedItems.length > 0) {
            setCartItems(parsedItems);
          }
        }
      } catch (error) {
        console.error('Error parsing cart items from localStorage:', error);
      }
    }

    // SEO Schema.org JSON-LD for Checkout
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CheckoutPage",
      "name": "Sipariş Tamamla - Nox Sanat",
      "description": "Seçtiğiniz sanat eserlerini güvenli bir şekilde satın alın",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/checkout`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Ana Sayfa",
            "item": import.meta.env.VITE_SITE_URL || "https://example.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Galeri",
            "item": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/gallery`
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Sipariş",
            "item": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/checkout`
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const fetchArtworks = async () => {
    try {
      const { data, error } = await supabase
        .from('artworks')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setArtworks(data && data.length > 0 ? data : mockArtworks);
    } catch (error) {
      console.error('Error fetching artworks:', error);
      setArtworks(mockArtworks);
    } finally {
      setIsLoading(false);
    }
  };

  // Fiyat formatlama fonksiyonu
  const formatPrice = (price: string | number) => {
    if (typeof price === 'string' && price.includes('TL+KDV')) {
      return price;
    }
    
    const numericPrice = typeof price === 'string' ? parseFloat(price.replace(/[^\d]/g, '')) : price;
    return `${numericPrice.toLocaleString('tr-TR')} TL+KDV`;
  };

  // Sayısal fiyat alma fonksiyonu
  const getNumericPrice = (price: string | number) => {
    return typeof price === 'string' ? parseFloat(price.replace(/[^\d]/g, '')) : price;
  };

  const cartArtworks = artworks.filter(artwork => cartItems.includes(artwork.id));
  const subtotal = cartArtworks.reduce((total, artwork) => total + getNumericPrice(artwork.price), 0);
  const kdv = subtotal * 0.20; // %20 KDV
  const total = subtotal + kdv;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Sipariş verilerini Supabase'e kaydet
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .insert([{
          customer_name: `${formData.firstName} ${formData.lastName}`,
          customer_email: formData.email,
          customer_phone: formData.phone,
          shipping_address: formData.address,
          shipping_city: formData.city,
          subtotal: subtotal,
          tax_amount: kdv,
          total_amount: total,
          payment_method: formData.paymentMethod,
          notes: formData.notes,
          estimated_delivery: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 5 gün sonra
        }])
        .select()
        .single();

      if (orderError) throw orderError;

      // Sipariş ürünlerini kaydet
      const orderItemsData = cartArtworks.map(artwork => ({
        order_id: orderData.id,
        artwork_id: artwork.id,
        artwork_title: artwork.title,
        artwork_artist: artwork.artist,
        artwork_size: artwork.size,
        artwork_price: getNumericPrice(artwork.price),
        artwork_image_url: artwork.image_url
      }));

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItemsData);

      if (itemsError) throw itemsError;

      // Sipariş durumu geçmişi ekle
      const { error: historyError } = await supabase
        .from('order_status_history')
        .insert([{
          order_id: orderData.id,
          status: 'preparing',
          notes: 'Sipariş alındı ve hazırlanmaya başlandı'
        }]);

      if (historyError) throw historyError;

      // Form gönderimi (isteğe bağlı - e-posta bildirimi için)
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('firstName', formData.firstName);
      formDataToSend.append('lastName', formData.lastName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('address', formData.address);
      formDataToSend.append('city', formData.city);
      formDataToSend.append('postalCode', formData.postalCode);
      formDataToSend.append('paymentMethod', formData.paymentMethod);
      formDataToSend.append('notes', formData.notes);
      formDataToSend.append('orderNumber', orderData.order_number);
      
      const orderItemsText = cartArtworks.map(artwork => 
        `${artwork.title} - ${artwork.artist} (${artwork.size}) - ${formatPrice(artwork.price)}`
      ).join(', ');
      formDataToSend.append('orderItems', orderItemsText);
      formDataToSend.append('subtotal', `${subtotal.toLocaleString('tr-TR')} TL`);
      formDataToSend.append('kdv', `${kdv.toLocaleString('tr-TR')} TL`);
      formDataToSend.append('total', `${total.toLocaleString('tr-TR')} TL`);

      // E-posta bildirimi gönder (isteğe bağlı)
      try {
        await fetch('https://readdy.ai/api/form/d3hu2ndsoafcrsrcpvq0', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formDataToSend.toString()
        });
      } catch (emailError) {
        console.log('E-posta bildirimi gönderilemedi:', emailError);
        // E-posta hatası sipariş işlemini etkilemez
      }

      setShowSuccess(true);
      // Sepeti temizle
      localStorage.removeItem('cartItems');
      // Form verilerini temizle
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        postalCode: '',
        paymentMethod: 'bank-transfer',
        notes: ''
      });

    } catch (error) {
      console.error('Error submitting order:', error);
      alert('Sipariş gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const navigateToHome = () => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/');
    }
  };

  const navigateToGallery = () => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/gallery');
    }
  };

  const scrollToSection = (sectionId: string) => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-gray-300">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header 
          cartCount={0}
          onHomeClick={navigateToHome}
          onAboutClick={() => scrollToSection('about-section')}
          onContactClick={() => scrollToSection('contact')}
        />
        
        <div className="pt-32 pb-16">
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-2xl mx-auto">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-green-600 rounded-full">
                <i className="ri-check-line text-4xl text-white"></i>
              </div>
              <h1 className="text-4xl font-bold mb-6">Siparişiniz Alındı!</h1>
              <p className="text-xl text-gray-300 mb-8">
                Siparişiniz başarıyla alındı. En kısa sürede sizinle iletişime geçeceğiz.
              </p>
              <div className="bg-gray-900 rounded-lg p-6 mb-8">
                <h3 className="text-lg font-bold mb-4">Sipariş Özeti</h3>
                <div className="space-y-2 text-left">
                  {cartArtworks.map((artwork) => (
                    <div key={artwork.id} className="flex justify-between items-center py-2 border-b border-gray-700 last:border-b-0">
                      <span className="text-gray-300">{artwork.title} - {artwork.artist}</span>
                      <span className="font-medium">{formatPrice(artwork.price)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center py-2 font-bold text-lg border-t border-gray-600 mt-4 pt-4">
                    <span>Toplam (KDV Dahil):</span>
                    <span>{total.toLocaleString('tr-TR')} TL</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-4 justify-center">
                <button 
                  onClick={navigateToHome}
                  className="bg-white text-black px-8 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Ana Sayfaya Dön
                </button>
                <button 
                  onClick={navigateToGallery}
                  className="bg-gray-800 text-white px-8 py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Galeriye Dön
                </button>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header 
          cartCount={0}
          onHomeClick={navigateToHome}
          onAboutClick={() => scrollToSection('about-section')}
          onContactClick={() => scrollToSection('contact')}
        />
        
        <div className="pt-32 pb-16">
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-gray-800 rounded-full">
                <i className="ri-shopping-bag-line text-4xl text-gray-400"></i>
              </div>
              <h1 className="text-3xl font-bold mb-4">Sepetiniz Boş</h1>
              <p className="text-gray-400 mb-8">
                Sipariş vermek için önce sepetinize eser eklemeniz gerekiyor.
              </p>
              <button 
                onClick={navigateToGallery}
                className="bg-white text-black px-8 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Galeriye Git
              </button>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Header 
        cartCount={cartItems.length}
        onHomeClick={navigateToHome}
        onAboutClick={() => scrollToSection('about-section')}
        onContactClick={() => scrollToSection('contact')}
      />
      
      {/* Hero Section */}
      <section 
        className="relative pt-24 pb-16 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.8)), url('https://readdy.ai/api/search-image?query=Elegant%20checkout%20process%20background%20with%20art%20gallery%20atmosphere%2C%20sophisticated%20payment%20and%20order%20completion%20scene%2C%20professional%20business%20transaction%20environment%20with%20artistic%20elements%2C%20secure%20shopping%20experience%20visualization&width=1920&height=600&seq=checkouthero&orientation=landscape')`
        }}
      >
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Sipariş Tamamla</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Seçtiğiniz sanat eserlerini güvenli bir şekilde satın alın
          </p>
        </div>
      </section>

      {/* Checkout Content */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              
              {/* Sipariş Formu */}
              <div>
                <h2 className="text-3xl font-bold mb-8">Sipariş Bilgileri</h2>
                
                <form id="siparis-formu" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Ad *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Soyad *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      E-posta *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Adres *
                    </label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent resize-none"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">{formData.address.length}/500 karakter</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Şehir *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Posta Kodu
                      </label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Ödeme Yöntemi *
                    </label>
                    <select
                      name="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent pr-8"
                      required
                    >
                      <option value="bank-transfer">Havale/EFT</option>
                      <option value="credit-card">Kredi Kartı</option>
                      <option value="cash-on-delivery">Kapıda Ödeme</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Sipariş Notları
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={3}
                      maxLength={500}
                      placeholder="Özel istekleriniz veya notlarınız..."
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">{formData.notes.length}/500 karakter</p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-white text-black py-4 rounded-lg font-bold text-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sipariş Gönderiliyor...' : 'Siparişi Tamamla'}
                  </button>
                </form>
              </div>

              {/* Sipariş Özeti */}
              <div>
                <h2 className="text-3xl font-bold mb-8">Sipariş Özeti</h2>
                
                <div className="bg-gray-900 rounded-lg p-6">
                  <div className="space-y-4 mb-6">
                    {cartArtworks.map((artwork) => (
                      <div key={artwork.id} className="flex items-center gap-4 pb-4 border-b border-gray-700 last:border-b-0">
                        <img 
                          src={artwork.image_url || "https://readdy.ai/api/search-image?query=Abstract%20art%20painting%20placeholder&width=80&height=100&seq=orderplaceholder&orientation=portrait"}
                          alt={artwork.title}
                          className="w-16 h-20 object-cover object-top rounded"
                        />
                        <div className="flex-1">
                          <h3 className="font-bold text-lg">{artwork.title}</h3>
                          <p className="text-gray-400 text-sm">{artwork.artist}</p>
                          <p className="text-gray-500 text-xs">{artwork.technique} - {artwork.size}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg">{formatPrice(artwork.price)}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-3 pt-4 border-t border-gray-700">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Ara Toplam:</span>
                      <span className="font-medium">{subtotal.toLocaleString('tr-TR')} TL</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">KDV (%20):</span>
                      <span className="font-medium">{kdv.toLocaleString('tr-TR')} TL</span>
                    </div>
                    <div className="flex justify-between items-center text-xl font-bold pt-3 border-t border-gray-600">
                      <span>Toplam:</span>
                      <span>{total.toLocaleString('tr-TR')} TL</span>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-gray-800 rounded-lg">
                    <div className="flex items-start gap-3">
                      <i className="ri-information-line text-blue-400 text-lg mt-0.5"></i>
                      <div className="text-sm text-gray-300">
                        <p className="font-medium mb-2">Önemli Bilgiler:</p>
                        <ul className="space-y-1 text-xs">
                          <li>• Tüm fiyatlar KDV dahildir</li>
                          <li>• Kargo ücreti ayrıca hesaplanacaktır</li>
                          <li>• Siparişiniz 1-3 iş günü içinde hazırlanır</li>
                          <li>• Ödeme onayı sonrası kargo süreci başlar</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
